/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useEffect, useRef } from "react";
import { PrimaryButton } from '@fluentui/react';
import useAxios from 'axios-hooks';
import TextField from "../../../Forms/TextField";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import { jsx } from "@emotion/react";
import { css } from '@emotion/react';
import { ColorPicker } from "../../../Util/ColorPicker";

const colorbox = css`
    width: 40px;
    height: 36px;
    border: 1px solid #aaaaaa;
    border-radius: 4px;
`;

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const Colors = ({ values, setFieldValue }) => {
    const [logoCobrandCopy, setLogoCobrandCopy] = useState(true);
    const [primary, setPrimary] = useState();
    const [secondary, setSecondary] = useState();

    const [{ data: cobrands, loading: loadingCobrands }] = useAxios('/internal/cobrands');
    const cobrandsOptions = cobrands && cobrands.map(u => ({ value: u.cobrandId, label: u.name }));

    const [{ data: cobrandSettings, loading: loadingCobrandSettings }, fetchCobrandSettings] = useAxios(
        { url: '/internal/cobrand/launcher', method: 'GET' },
        { manual: true }
    );

    const [{ data: palette, loading: loadingPalette }, fetchPalette] = useAxios(
        { url: '/internal/cobrand/colors/suggest', method: 'GET' },
        { manual: true }
    );

    const handleCobrandChange = async (option, setFieldValue) => {
        setLogoCobrandCopy(option.value);
        const selectedCobrandId = option.value;

        const result = await fetchCobrandSettings({
            url: `/internal/cobrand/launcher/${selectedCobrandId}`,
        });

        if (!result.error) {
            const settings = result.data;
            setFieldValue('a1', settings.a1);
            setFieldValue('a2', settings.a2);
            setFieldValue('a3', settings.a3);
            setFieldValue('a4', settings.a4);
            setFieldValue('a5', settings.a5);
            setFieldValue('a6', settings.a6);
            setFieldValue('a7', settings.a7);
            setFieldValue('b1', settings.b1);
            setFieldValue('b2', settings.b2);
            setFieldValue('b3', settings.b3);
            setFieldValue('b4', settings.b4);
            setFieldValue('b5', settings.b5);
            setFieldValue('b6', settings.b6);
            setFieldValue('b7', settings.b7);
            setFieldValue('c1', settings.c1 || '4857E0');
            setFieldValue('c2', settings.c2 || '4857E0');
        } else {
            setShowError(true);
        }
    };

    const generatePalette = async (setFieldValue) => {
        const result = await fetchPalette({
            url: `/internal/cobrand/colors/suggest?PrimaryColorHex=${primary}&SecondaryColorHex=${secondary}`,
        });

        if (!result.error) {
            const settings = result.data.suggestedColors;
            setFieldValue('a1', settings.a1);
            setFieldValue('a2', settings.a2);
            setFieldValue('a3', settings.a3);
            setFieldValue('a4', settings.a4);
            setFieldValue('a5', settings.a5);
            setFieldValue('a6', settings.a6);
            setFieldValue('a7', settings.a7);
            setFieldValue('b1', settings.b1);
            setFieldValue('b2', settings.b2);
            setFieldValue('b3', settings.b3);
            setFieldValue('b4', settings.b4);
            setFieldValue('b5', settings.b5);
            setFieldValue('b6', settings.b6);
            setFieldValue('b7', settings.b7);
            setFieldValue('c1', settings.c1 || '4857E0');
            setFieldValue('c2', settings.c2 || '4857E0');
        } else {
            setShowError(true);
        }
    };

    function hexToLuminance(hex) {
        if (!hex || hex.length !== 6) return 1;
        const rgb = hex.match(/.{2}/g).map(c => parseInt(c, 16) / 255);
        const [r, g, b] = rgb.map(c =>
            c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
        );
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    function getContrastRatio(hex1, hex2) {
        const lum1 = hexToLuminance(hex1);
        const lum2 = hexToLuminance(hex2);
        const [L1, L2] = [lum1, lum2].sort((a, b) => b - a);
        return (L1 + 0.05) / (L2 + 0.05);
    }

    const contrastChecks = [
        ['a2', 'a1'],
        ['b2', 'b1'],
        ['a2', 'a3'],
        ['b2', 'b3'],
        ['a5', 'a1'],
        ['b5', 'b1'],
        ['a6', 'ffffff'],
        ['a6', 'f5f5f5'],
        ['b6', 'ffffff'],
        ['b6', 'f5f5f5'],
        ['a7', '1a1a1a'],
        ['b7', '1a1a1a'],
        ['c1', 'ffffff'],
        ['c2', 'ffffff'],
    ];

    const failingContrastFields = new Set();
    contrastChecks.forEach(([f1, f2]) => {
        const v1 = values[f1]?.toLowerCase?.() ?? '';
        const v2 = values[f2]?.toLowerCase?.() ?? '';
        if (v1.length === 6 && v2.length === 6 && getContrastRatio(v1, v2) < 4.5) {
            failingContrastFields.add(f1);
        }
    });

    const emptyOrWhiteFields = new Set();
    ['c1', 'c2'].forEach((fieldKey) => {
        const value = values[fieldKey]?.toLowerCase?.() ?? '';
        if (!value || value === 'ffffff') {
            emptyOrWhiteFields.add(fieldKey);
        }
    });

    const handleColorBlur = (fieldKey, colorValue) => {
        const value = values[fieldKey]?.toLowerCase?.() ?? '';
        if (!value || value.toLowerCase() === 'ffffff') {
            setFieldValue(fieldKey, colorValue);
        }
    };

    return (
        <div className="panel panel-default">
            <div className="panel-heading panel-heading-cobrand"><div className='panel-h'>Colors / Styles</div>
                <div className="cobrand-select">
                    <Select
                        components={{ MenuList: CustomMenuList }}
                        options={cobrandsOptions}
                        placeholder="Copy from Cobrand"
                        name="clientId"
                        value={cobrandsOptions?.find(
                            (option) => option.value === logoCobrandCopy
                        )}
                        onChange={(option) => {
                            handleCobrandChange(option, setFieldValue);
                        }}
                        isSearchable
                        styles={{
                            menuList: (base) => ({
                                ...base,
                                maxHeight: "300px",
                                overflowY: "auto",
                            }),
                            container: (base) => ({
                                ...base,
                                width: 200,
                            }),
                            control: (base) => ({
                                ...base,
                                border: "1px solid #aaaaaa",
                            }),
                        }}
                    />
                </div>
            </div>
            <div className="panel-body">

                <fieldset style={{ marginBottom: 30 }}>
                    <div className="displayflex" style={{ justifyContent: 'center' }}>
                        <div>
                            <div className="form-inline">
                                Primary Color
                                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <ColorPicker defaultColor={`#${primary || 'ffffff'}`} setDefaultColor={(c) => setPrimary(c.replace("#", ""))} key={'primary' + secondary} />
                                    <TextField
                                        name='a1'
                                        value={primary}
                                        onChange={(e, val) => setPrimary(val?.slice(0, 6) || '')}
                                        placeholder="6 character HEX code"
                                        maxLength={6}
                                        required
                                        styles={{ root: { width: 300 }, fieldGroup: { width: 180 } }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ marginLeft: 80 }}>
                            <div className="form-inline">
                                Secondary Color
                                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <ColorPicker defaultColor={`#${secondary || 'ffffff'}`} setDefaultColor={(c) => setSecondary(c.replace("#", ""))} key={'secondary' + secondary} />

                                    <TextField
                                        name='b1'
                                        value={secondary}
                                        onChange={(e, val) => setSecondary(val?.slice(0, 6) || '')}
                                        placeholder="6 character HEX code"
                                        maxLength={6}
                                        required
                                        styles={{ root: { width: 300 }, fieldGroup: { width: 180 } }}
                                    />

                                </div>
                            </div>
                            <div className="displayflex mt15" style={{ justifyContent: "flex-end" }}>
                                <PrimaryButton
                                    disabled={!primary || !secondary}
                                    onClick={() => generatePalette(setFieldValue)}
                                    text="Generate Palette"
                                />
                            </div>
                        </div>
                    </div>

                </fieldset>
                <fieldset>
                    <div className="displayflex" style={{ justifyContent: 'center' }}>
                        <div>
                            {[
                                'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'c1',
                            ].map((fieldKey) => (
                                <div className="form-inline" key={fieldKey}>
                                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        {fieldKey.toUpperCase()}
                                        <TextField
                                            name={fieldKey}
                                            value={values[fieldKey] || ''}
                                            onChange={(e, val) => setFieldValue(fieldKey, val?.slice(0, 6) || '')}
                                            placeholder="6 character HEX code"
                                            maxLength={6}
                                            required
                                            styles={{ root: { width: 300 }, fieldGroup: { width: 180 } }}
                                            onBlur={fieldKey === 'c1' ? () => handleColorBlur('c1', '4857E0') : undefined}
                                        />
                                        <ColorPicker defaultColor={`#${values[fieldKey] || 'ffffff'}`} setDefaultColor={(c) => {
                                            setFieldValue(fieldKey, c.replace("#", ""))
                                        }} key={fieldKey + values[fieldKey]} />
                                    </div>
                                    {failingContrastFields.has(fieldKey) && (
                                        <div style={{ color: 'red', marginLeft: 60, fontSize: 12 }}>
                                            Fails WCAG AA contrast check
                                        </div>
                                    )}
                                    {emptyOrWhiteFields.has(fieldKey) && (
                                        <div style={{ color: 'red', marginLeft: 60, fontSize: 12 }}>
                                            Cannot be empty or white (#FFFFFF)
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div style={{ marginLeft: 50 }}>
                            {[
                                'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'c2', 
                            ].map((fieldKey) => (
                                <div className="form-inline" key={fieldKey}>
                                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        {fieldKey.toUpperCase()}
                                        <TextField
                                            name={fieldKey}
                                            value={values[fieldKey] || ''}
                                            onChange={(e, val) => setFieldValue(fieldKey, val?.slice(0, 6) || '')}
                                            placeholder="6 character HEX code"
                                            maxLength={6}
                                            required
                                            styles={{ root: { width: 300 }, fieldGroup: { width: 180 } }}
                                            onBlur={fieldKey === 'c2' ? () => handleColorBlur('c2', '4857E0') : undefined}
                                        />
                                        <div
                                            css={colorbox}
                                            style={{
                                                backgroundColor: `#${values[fieldKey] || 'ffffff'}`
                                            }}
                                        />
                                    </div>
                                    {emptyOrWhiteFields.has(fieldKey) && (
                                        <div style={{ color: 'red', marginLeft: 60, fontSize: 12 }}>
                                            Cannot be empty or white (#FFFFFF)
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                </fieldset>

            </div>
        </div>
    );
};

export default Colors;
